import { useCallback, useEffect, useState } from "react";
import { useScopeParams } from "@/hooks/useScopeParams";
import { Table, Tooltip, Button, Tabs } from "antd";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import { CloseOutlined } from "@ant-design/icons";

import { getColumnConfig } from "./DosingTrackerConfigTable";
import {
  getLastKeyValue,
  checkFiltersAreEmpty,
} from "../../../utils/tableConfigs/ActivitiesTableUtilFunctions";
import InfoTooltip from "../../../components/UI/InfoTooltip";
import TooltipTag from "../../../components/UI/TooltipTag";
import StatusPill from "../../../components/UI/StatusPill";
import TableDowloadButton from "../../../components/UI/DownloadTable";
import Error403 from "../../../components/UI/Error403";
import Error from "../../../components/UI/Error";
import {
  pillColor,
  textColor,
  tooltipTitle,
} from "../../../utils/tableConfigs/DosingMapping";
import { NO_TIMEPOINT_COLUMNS } from "../../../utils/tableConfigs/DosingConstants";
import {
  administeredTableLegend,
  statusTableLegend,
} from "../../../utils/Legends";

import { useFetch, is403ApiError } from "../../../hooks/FetchHook";
import Loader from "@/components/UI/Loader";

const timepointSafetyColumns = [
  {
    title: "Column 1",
    dataIndex: "Column1",
    key: "Column1",
  },
  {
    title: "Column 2",
    dataIndex: "Column2",
    key: "Column2",
    render: (text: any) => {
      if (text === null) return "-";
      return (
        <StatusPill
          text={text}
          textColor={textColor}
          pillColor={pillColor}
          tooltipTitle={tooltipTitle}
        />
      );
    },
  },
];

const formatSafetyDataForTable = (data: any) => {
  const mapAllowedKeys = {
    activity_status: "Administration Status",
    date: "Date",
    time: "Time",
    total_vol_admin_yn: "Total Volume Administered?",
    reas_vol_not_admin: "Reason why Total Volume was Not Administered",
    dose_altered_ae: "Dose Altered Due to AE?",
    reason_altered: "Reason for Dose Alteration",
    admin_loc: "Location Administered",
  };

  const formattedData: any[] = [];
  if (data === undefined) return formattedData;
  for (const [key, value] of Object.entries(data)) {
    if (Object.hasOwn(mapAllowedKeys, key)) {
      formattedData.push({
        Column1: (mapAllowedKeys as any)[key],
        Column2: value,
      });
    }
  }
  return formattedData;
};

const additionalInfoColumns = [
  {
    title: "Measure",
    dataIndex: "Column1",
    key: "Column1",
  },
  {
    title: "EDC",
    dataIndex: "Column2",
    key: "Column2",
    render: (text: any) => (
      <StatusPill
        text={text}
        textColor={textColor}
        pillColor={pillColor}
        tooltipTitle={tooltipTitle}
        type="float"
      />
    ),
  },
  {
    title: "Derived by Vivo",
    dataIndex: "Column3",
    key: "Column3",
    render: (text: any) => (
      <StatusPill
        text={text}
        textColor={textColor}
        pillColor={pillColor}
        tooltipTitle={tooltipTitle}
        type="float"
      />
    ),
  },
];

const formatAdditionalInfoForTable = (data: any) => {
  const firstColumn = [
    "Expected Dose (mg)",
    "Administered Dose (mg/kg)",
    "Expected Volume (mL)",
    "Actual Volume (mL)",
  ];
  const formattedData: any[] = [];
  if (data === undefined) return formattedData;
  formattedData.push({
    Column1: firstColumn[0],
    Column2: data.dose_mg_edc ? data.dose_mg_edc : "-",
    Column3: data.dose_mg_vivo ? data.dose_mg_vivo : "-",
  });
  formattedData.push({
    Column1: firstColumn[1],
    Column2: "-",
    Column3: data.actual_dose_mg_kg_vivo ? data.actual_dose_mg_kg_vivo : "-",
  });
  formattedData.push({
    Column1: firstColumn[2],
    Column2: data.expected_volume_ml_edc ? data.expected_volume_ml_edc : "-",
    Column3: data.expected_volume_ml_vivo ? data.expected_volume_ml_vivo : "-",
  });
  formattedData.push({
    Column1: firstColumn[3],
    Column2: data.actual_volume_ml_edc ? data.actual_volume_ml_edc : "-",
    Column3: data.actual_volume_ml_vivo ? data.actual_volume_ml_vivo : "-",
  });
  return formattedData;
};

const createTotalDosageColumn = (valueData: any) => {
  if (!Array.isArray(valueData) || valueData.length === 0) {
    return [];
  }

  // Create total_dosage column for valueData

  const dataColumns = Object.keys(valueData[0]);
  const columnsToSum = dataColumns.filter(
    (col) => !NO_TIMEPOINT_COLUMNS.has(col),
  );

  // Calculate total for each row
  return valueData.map((row) => {
    const total = columnsToSum
      .reduce((sum, col) => sum + (parseFloat(row[col]) || 0), 0)
      .toFixed(1); // Round to 1 decimal place

    // Build the new row object with total_dosage inserted after study_status
    const newRow: Record<string, any> = {};
    for (const key in row) {
      let value = row[key];
      if (
        (key.includes("Week") || key.includes("Day")) &&
        !isNaN(Number(value))
      ) {
        value = parseFloat(value).toFixed(2);
      }

      newRow[key] = value;

      if (key === "study_status") {
        newRow["total_dosage"] = total; // Insert total_dosage right after study_status
      }
    }
    return newRow;
  });
};

const mergeTotalDosageColumn = (statusData: any, valueData: any) => {
  if (!Array.isArray(statusData) || statusData.length === 0) {
    return [];
  }

  if (!Array.isArray(valueData)) {
    valueData = [];
  }

  //Insert the total_dosage column in the statusData
  return statusData.map((statusItem) => {
    const valueItem = valueData.find(
      (valueItem: any) => valueItem.subject === statusItem.subject,
    );
    const newStatusItem: Record<string, any> = {};
    for (const key in statusItem) {
      newStatusItem[key] = statusItem[key];
      if (key === "study_status") {
        newStatusItem["total_dosage"] = valueItem
          ? valueItem.total_dosage
          : null;
      }
    }
    return newStatusItem;
  });
};

export default function DosingTracker() {
  const {
    data: statusData,
    isLoading: dosingByStatusLoading,
    isError: dosingByStatusError,
    error: statusErrorObject,
  } = useFetch({
    endpointSuffix: "dosing/by-status/",
  });

  const {
    data: valueData,
    isLoading: dosingByValueLoading,
    isError: dosingByValueError,
    error: valueErrorObject,
  } = useFetch({
    endpointSuffix: "dosing/by-value/",
  });

  const loading = dosingByStatusLoading || dosingByValueLoading;
  const error = dosingByStatusError || dosingByValueError;

  const { id } = useScopeParams();
  const path = `/${id}/subjects/overview`;

  const [allDosingByStatus, setAllDosingByStatus] = useState<any[]>([]);
  const [dosingByStatus, setDosingByStatus] = useState<any[]>([]);
  const [allDosingByValue, setAllDosingByValue] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dosingByValue, setDosingByValue] = useState<any[]>([]);
  const [selectedCell, setSelectedCell] = useState<any>(null);
  const [showValue, setShowValue] = useState("value");
  const [tableHeight, setTableHeight] = useState(window.innerHeight - 400);
  const [tableFiltersByStatus, setTableFiltersByStatus] = useState<
    Record<string, any>
  >({});
  const [tableFiltersByValue, setTableFiltersByValue] = useState<
    Record<string, any>
  >({});
  const [filteredDataByStatus, setFilteredDataByStatus] = useState<any[]>([]);
  const [filteredDataByValue, setFilteredDataByValue] = useState<any[]>([]);

  const mergeDosingData = useCallback(async () => {
    // Create total_dosage column for valueData
    const dataWithTotal = createTotalDosageColumn(valueData);
    setDosingByValue(dataWithTotal);
    setAllDosingByValue(dataWithTotal);

    // Merge total_dosage into statusData
    const mergedData = mergeTotalDosageColumn(statusData, dataWithTotal);
    setDosingByStatus(mergedData);
    setAllDosingByStatus(mergedData);
  }, [statusData, valueData]);

  useEffect(() => {
    mergeDosingData();
  }, [mergeDosingData]);

  useEffect(() => {
    const handleResize = () => {
      setTableHeight(window.innerHeight - 450);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Event handler for table cell click
  const handleCellClick = (columnName: any, value: any, record: any) => {
    const cellInfo = {
      subject: record.subject,
      site: record.site,
      status: record.study_status,
      [columnName]: value,
    };
    setSelectedCell(cellInfo);
    setDrawerOpen(true);
    //Get the last key value pair of the selected cell that corresponds to the visit week

    // fetchSingleSubjectData(cellInfo.Subject, visitType.key);
  };

  //Get the last key value pair of the selected cell that corresponds to the visit week
  const visitType = getLastKeyValue(selectedCell ? selectedCell : {});

  const statusColumns =
    dosingByStatus.length > 0
      ? getColumnConfig(
          dosingByStatus,
          handleCellClick,
          tableFiltersByStatus,
          path,
          "status",
        )
      : [];

  const valueColumns =
    dosingByValue.length > 0
      ? getColumnConfig(
          dosingByValue,
          handleCellClick,
          tableFiltersByValue,
          path,
          "value",
        )
      : [];

  //Event handler for the table filters
  const onChangeFiltersByValue = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: any,
  ) => {
    const emptyFilters = checkFiltersAreEmpty(filters);
    setTableFiltersByValue(filters);

    if (extra.action === "filter") {
      //Filter the data according to the filters
      if (emptyFilters) {
        setDosingByValue(allDosingByValue);
        setFilteredDataByValue([]);
      } else {
        setFilteredDataByValue(extra.currentDataSource);
      }
    }
  };

  //Event handler for the table filters
  const onChangeTableFiltersByStatus = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: any,
  ) => {
    const emptyFilters = checkFiltersAreEmpty(filters);
    setTableFiltersByStatus(filters);

    if (extra.action === "filter") {
      //Filter the data according to the filters
      if (emptyFilters) {
        setDosingByStatus(allDosingByStatus);
        setFilteredDataByStatus([]);
      } else {
        setFilteredDataByStatus(extra.currentDataSource);
      }
    }
  };

  //Event handler for the "Clear all" button
  const handleClearAll = () => {
    if (showValue === "value") {
      for (const filter of Object.keys(tableFiltersByValue)) {
        tableFiltersByValue[filter] = null;
      }
      setFilteredDataByValue([]);
      setTableFiltersByValue({});
    } else {
      for (const filter of Object.keys(tableFiltersByStatus)) {
        tableFiltersByStatus[filter] = null;
      }
      setFilteredDataByStatus([]);
      setTableFiltersByStatus({});
    }
  };

  const items = [
    {
      key: "value",
      label: "Dose Administration",
      children: (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h1 className="text-[15px] font-semibold">Dose Administered</h1>
              <InfoTooltip title="View a high level overview of the amount of study intervention administered per subject per timepoint. Click the cell for detailed dosing information." />
            </div>
            <div className="flex gap-3 items-center">
              <TableDowloadButton
                data={showValue === "value" ? dosingByValue : dosingByStatus}
                name={`dosing_tracker_dose_administered`}
              />
            </div>
          </div>
          <div className="flex justify-between items-center mb-1">
            <div className="flex gap-4">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClearAll}
              >
                Clear all filters
              </Button>
            </div>
          </div>
          <div className="flex bg-white items-center p-2 gap-4 rounded-[4px] mb-2 shadow-xs">
            {administeredTableLegend.map((legend) => (
              <Tooltip title={legend.tooltip} key={legend.title}>
                <div className="flex gap-3 items-center">
                  {legend.icon}
                  <p>{legend.title}</p>
                </div>
              </Tooltip>
            ))}
          </div>
          <Table
            bordered
            showSorterTooltip={false}
            className="shadow-md"
            columns={valueColumns as any}
            dataSource={dosingByValue}
            loading={{
              indicator: (
                <div>
                  <Loader noMargin tableLoader />
                </div>
              ),
              spinning: loading,
            }}
            rowKey={(record) => record.participant_id as string}
            scroll={{
              x: 1500,
              y: tableHeight,
            }}
            size="small"
            pagination={false}
            virtual={true}
            onChange={onChangeFiltersByValue}
            footer={() => (
              <div className="flex gap-4">
                <p className="font-bold">
                  Total records: {allDosingByValue.length}
                </p>
                {filteredDataByValue.length > 0 ? (
                  <p className="font-bold">
                    Selected records: {filteredDataByValue.length}
                  </p>
                ) : (
                  ""
                )}
              </div>
            )}
          />
        </div>
      ),
    },
    {
      key: "status",
      label: "Dose Status",
      children: (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h1 className="text-[15px] font-semibold">Dose Status</h1>
              <InfoTooltip title="View and track the dosing status per subject across all timepoints. Click the cell for detailed dosing information at the chosen timepoint." />
            </div>
            <div className="flex gap-3 items-center">
              <TableDowloadButton
                data={showValue === "value" ? dosingByValue : dosingByStatus}
                name={`dosing_tracker_dose_status`}
              />
            </div>
          </div>
          <div className="flex justify-between items-center mb-1">
            <div className="flex gap-4">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClearAll}
              >
                Clear all filters
              </Button>
            </div>
          </div>
          <div className="flex bg-white items-center p-2 gap-4 rounded-[4px] mb-2 shadow-xs">
            {statusTableLegend.map((legend) => (
              <Tooltip title={legend.tooltip} key={legend.title}>
                <div className="flex gap-3 items-center">
                  {legend.icon}
                  <p>{legend.title}</p>
                </div>
              </Tooltip>
            ))}
          </div>
          <Table
            bordered
            showSorterTooltip={false}
            className="shadow-md"
            columns={statusColumns as any}
            dataSource={dosingByStatus}
            loading={{
              indicator: (
                <div>
                  <Loader noMargin tableLoader />
                </div>
              ),
              spinning: loading,
            }}
            rowKey={(record) => record.participant_id as string}
            scroll={{
              x: 1500,
              y: tableHeight,
            }}
            size="small"
            pagination={false}
            virtual={true}
            onChange={onChangeTableFiltersByStatus}
            footer={() => (
              <div className="flex gap-4">
                <p className="font-bold">
                  Total records: {allDosingByStatus.length}
                </p>
                {filteredDataByStatus.length > 0 ? (
                  <p className="font-bold">
                    Selected records: {filteredDataByStatus.length}
                  </p>
                ) : (
                  ""
                )}
              </div>
            )}
          />
        </div>
      ),
    },
  ];

  const onChangeTab = (key: any) => {
    setShowValue(key);
  };

  if (error) {
    if (is403ApiError(statusErrorObject) || is403ApiError(valueErrorObject)) {
      return (
        <div className="flex mt-[100px] justify-center items-center">
          <Error403 />
        </div>
      );
    }
    return <Error />;
  }

  return (
    <div className="p-4">
      <SubjectDrawer
        selectedCell={selectedCell}
        visitType={visitType}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
      />
      <div className="flex mt-4 mb-5 items-center justify-between">
        <div className="flex gap-2 items-center">
          <h1 className="text-[18px] font-bold">Safety: Dosing Tracker</h1>
        </div>
      </div>

      <Tabs defaultActiveKey="value" items={items} onChange={onChangeTab} />
    </div>
  );
}

const SubjectDrawer = ({
  selectedCell,
  visitType,
  drawerOpen,
  setDrawerOpen,
}: any) => {
  const encodedVisitType = encodeURIComponent(visitType.key);
  const { data, isLoading } = useFetch({
    endpointSuffix: `dosing_timepoint/${selectedCell?.subject}/?timepoint=${encodedVisitType}`,
  });

  const anyData = data as any;
  const formattedTimepointData = anyData
    ? formatSafetyDataForTable(anyData[0])
    : [];

  const additionalInformation = anyData
    ? formatAdditionalInfoForTable(anyData[0])
    : [];
  const headersArray = [
    { key: "TIMEPOINT", value: visitType.key || "-" },
    {
      key: "WEIGHT",
      value:
        anyData?.length > 0 && anyData[0].weight_kg
          ? `${anyData[0].weight_kg} kg`
          : "-",
    },
  ];

  return (
    <ResizableDrawer
      headerTitle={
        <div className="flex items-center gap-2">
          <h1 className="text-white">Subject {selectedCell?.subject}</h1>
          <TooltipTag
            tagColor={pillColor[selectedCell?.status]}
            textColor={textColor[selectedCell?.status]}
            tooltipTitle={tooltipTitle[selectedCell?.status]}
            tagStyle={{
              padding: "5px 20px",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            {selectedCell?.status}
          </TooltipTag>
        </div>
      }
      headersArray={headersArray}
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      sizeToggle={{
        collapsedWidth: 750,
        expandedWidth: 1250,
        initialWidth: 750,
      }}
      destroyOnHidden={true}
      closable={false}
      closeIcon={null}
    >
      <>
        {isLoading ? (
          <div className="flex mt-5 justify-center items-center">
            <Loader />
          </div>
        ) : (
          <>
            <h1 className="font-bold mb-2">Administration Information</h1>
            <Table
              bordered
              columns={timepointSafetyColumns}
              dataSource={formattedTimepointData}
              rowKey={(record) => record.Column1}
              showHeader={false}
              pagination={false}
            />

            <h1 className="font-bold mt-5 mb-2">Additional Information</h1>
            <Table
              bordered
              columns={additionalInfoColumns}
              dataSource={additionalInformation}
              rowKey={(record) => record.Column1}
              pagination={false}
            />

            {/* {formattedTimepointData.length > 0  &&  (
                <div className="flex flex-col items-center justify-center mt-5 mb-3">
                  <CalendarMonthIcon />
                  <p className="text-[#6e6c6c]">Visit Not Required</p>
                </div>
              )} */}
          </>
        )}
      </>
    </ResizableDrawer>
  );
};
